import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// Backend enums serialize as strings via JsonStringEnumConverter; the rest of
// the frontend expects numeric values. Normalize once at the API boundary.
const PRICING_MODE_MAP: Record<string, number> = { PerTray: 1, PerSeedlingOrPiece: 2 };
const TRAY_TYPE_MAP: Record<string, number> = { TwoTenHole: 210, FourFortyTwoHole: 442 };

function normalizePricing(raw: any): VarietyPricing {
  return {
    ...raw,
    pricingMode: typeof raw.pricingMode === "string"
      ? (PRICING_MODE_MAP[raw.pricingMode] ?? (Number(raw.pricingMode) || 1))
      : raw.pricingMode,
    trayType: raw.trayType == null ? null
      : typeof raw.trayType === "string"
        ? (TRAY_TYPE_MAP[raw.trayType] ?? (Number(raw.trayType) || null))
        : raw.trayType,
  };
}

export interface VarietyPricing {
  id: string;
  cropTypeId: string;
  cropTypeName: string;
  cropVarietyId?: string;
  varietyName?: string;
  pricingMode: number; // 1 = PerTray, 2 = PerSeedlingOrPiece
  pricingModeName: string;
  trayType?: number | null; // 210 or 442, null for per-seedling
  trayTypeName?: string | null;
  /** Physical hole capacity supplied by the backend tray catalogue. */
  trayCapacity?: number | null;
  /** Buffer-adjusted capacity; distinct from physical holes. */
  sellablePerTray?: number | null;
  // Either price may be null if that side is not offered for the variety.
  // At least one of the two must be set.
  companySeedlingPrice: number | null;
  propagationServicePrice: number | null;
  isActive: boolean;
  createdAt: string;
  hasCompanySeedling?: boolean;
  hasPropagationService?: boolean;
}

export interface CreateVarietyPricingRequest {
  cropTypeId: string;
  cropVarietyId: string;
  pricingMode: number; // 1 = PerTray, 2 = PerSeedlingOrPiece
  trayType?: number | null; // 210 or 442, required for PerTray, null for PerSeedlingOrPiece
  // Either may be omitted/null if that side is not offered. At least one must be set.
  companySeedlingPrice?: number | null;
  propagationServicePrice?: number | null;
}

export interface UpdateVarietyPricingRequest {
  id: string;
  // Either may be null if that side is not offered. At least one must be set.
  companySeedlingPrice: number | null;
  propagationServicePrice: number | null;
  isActive: boolean;
}

export interface UpsertBatchVarietyPricingEntry {
  trayType?: number | null;
  companySeedlingPrice: number;
  propagationServicePrice?: number | null;
}

export interface UpsertBatchVarietyPricingRequest {
  cropTypeId: string;
  cropVarietyId?: string | null;
  pricingMode: number; // 1 = PerTray, 2 = PerSeedlingOrPiece
  entries: UpsertBatchVarietyPricingEntry[];
}

export type VarietyPricingSortBy = "cropType" | "variety" | "createdAt";

export interface VarietyPricingListQuery {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: VarietyPricingSortBy;
  sortDirection?: "asc" | "desc";
  isActive?: boolean;
  cropTypeId?: string;
  cropVarietyId?: string;
}

export interface VarietyPricingListResult {
  items: VarietyPricing[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export const varietyPricingApi = {
  getVarietyPricingsPaged: async (
    query: VarietyPricingListQuery = {}
  ): Promise<VarietyPricingListResult> => {
    const params = new URLSearchParams();
    if (query.pageNumber != null) params.set("pageNumber", String(query.pageNumber));
    if (query.pageSize != null) params.set("pageSize", String(query.pageSize));
    if (query.search) params.set("search", query.search);
    if (query.sortBy) params.set("sortBy", query.sortBy);
    if (query.sortDirection) params.set("sortDirection", query.sortDirection);
    if (query.isActive != null) params.set("isActive", String(query.isActive));
    if (query.cropTypeId) params.set("cropTypeId", query.cropTypeId);
    if (query.cropVarietyId) params.set("cropVarietyId", query.cropVarietyId);
    const qs = params.toString();
    const { data } = await apiClient.get<ApiResponse<VarietyPricingListResult>>(
      qs ? `/VarietyPricings/paged?${qs}` : "/VarietyPricings/paged"
    );
    const result = data.data;
    result.items = result.items.map(normalizePricing);
    return result;
  },

  getAllVarietyPricings: async (): Promise<VarietyPricing[]> => {
    const { data } = await apiClient.get<ApiResponse<VarietyPricing[]>>(
      "/VarietyPricings"
    );
    return (data.data ?? []).map(normalizePricing);
  },

  getActiveVarietyPricings: async (): Promise<VarietyPricing[]> => {
    const { data } = await apiClient.get<ApiResponse<VarietyPricing[]>>(
      "/VarietyPricings/active"
    );
    return (data.data ?? []).map(normalizePricing);
  },

  getVarietyPricingByCropType: async (
    cropTypeId: string
  ): Promise<VarietyPricing[]> => {
    const { data } = await apiClient.get<ApiResponse<VarietyPricing[]>>(
      `/VarietyPricings/crop-type/${cropTypeId}`
    );
    return (data.data ?? []).map(normalizePricing);
  },

  getVarietyPricingByCropTypeAndVariety: async (
    cropTypeId: string,
    cropVarietyId?: string
  ): Promise<VarietyPricing[]> => {
    const url = cropVarietyId
      ? `/VarietyPricings/crop-type/${cropTypeId}/variety/${cropVarietyId}`
      : `/VarietyPricings/crop-type/${cropTypeId}/variety`;
    const { data } = await apiClient.get<ApiResponse<VarietyPricing[]>>(url);
    return (data.data ?? []).map(normalizePricing);
  },

  getVarietyPricingById: async (id: string): Promise<VarietyPricing> => {
    const { data } = await apiClient.get<ApiResponse<VarietyPricing>>(
      `/VarietyPricings/${id}`
    );
    return normalizePricing(data.data);
  },

  createVarietyPricing: async (
    payload: CreateVarietyPricingRequest
  ): Promise<VarietyPricing> => {
    const { data } = await apiClient.post<ApiResponse<VarietyPricing>>(
      "/VarietyPricings",
      payload
    );
    return normalizePricing(data.data);
  },

  updateVarietyPricing: async (
    payload: UpdateVarietyPricingRequest
  ): Promise<VarietyPricing> => {
    const { data } = await apiClient.put<ApiResponse<VarietyPricing>>(
      `/VarietyPricings/${payload.id}`,
      payload
    );
    return normalizePricing(data.data);
  },

  deleteVarietyPricing: async (id: string): Promise<void> => {
    await apiClient.delete(`/VarietyPricings/${id}`);
  },

  upsertBatchVarietyPricing: async (
    payload: UpsertBatchVarietyPricingRequest
  ): Promise<VarietyPricing[]> => {
    const { data } = await apiClient.post<ApiResponse<VarietyPricing[]>>(
      "/VarietyPricings/upsert-batch",
      payload
    );
    return (data.data ?? []).map(normalizePricing);
  },
};
